
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/auth';
import { ShieldCheck, ArrowRightIcon, LifeBuoy, TrendingUp, BriefcaseIcon } from 'lucide-react';
import EscortSubscriptionPlans from '@/components/subscription/EscortSubscriptionPlans';
import EscortServicesTiers from '@/components/subscription/EscortServicesTiers';
import RatesDisplay from '@/components/profile/tabs/RatesDisplay';
import { useToast } from '@/hooks/use-toast';

const EscortSubscription = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Redirect if user is not an escort or admin
  React.useEffect(() => {
    if (user && user.role !== 'escort' && user.role !== 'admin') {
      toast({
        title: 'Accès non autorisé',
        description: 'Cette page est réservée aux escorts et aux administrateurs.',
        variant: 'destructive',
      });
      navigate('/dashboard');
    }
  }, [user, navigate, toast]);

  return (
    <Layout>
      <div className="py-12 px-4 bg-muted/30 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Forfaits d'Abonnement pour Escorts</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Choisissez le forfait parfait pour augmenter votre visibilité et votre rentabilité
            </p>
          </div>

          {/* Display subscription plans */}
          <EscortSubscriptionPlans />
          
          {/* Information blocks */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-gradient-to-br from-pink-50 to-white">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <ShieldCheck className="mr-2 h-5 w-5 text-pink-500" />
                  Confidentialité et Sécurité
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Nos forfaits Premium et Élite incluent des fonctionnalités de sécurité avancées pour protéger votre identité et garantir votre tranquillité d'esprit.
                </p>
                <Button variant="outline" className="w-full mt-2" onClick={() => navigate('/security')}>
                  En savoir plus
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-white">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <TrendingUp className="mr-2 h-5 w-5 text-blue-500" />
                  Augmentez vos revenus
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Les forfaits Premium et Élite vous permettent de diversifier vos sources de revenus grâce à la vente de médias privés et de services additionnels.
                </p>
                <Button variant="outline" className="w-full mt-2" onClick={() => navigate('/tips')}>
                  Conseils de pro
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-white">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <LifeBuoy className="mr-2 h-5 w-5 text-purple-500" />
                  Support prioritaire
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Nos membres Premium et Élite bénéficient d'un accès privilégié à notre équipe de support pour une assistance rapide et efficace.
                </p>
                <Button variant="outline" className="w-full mt-2" onClick={() => navigate('/support')}>
                  Contacter le support
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </div>
          
          {/* Display services comparison table */}
          <div className="mt-16 mb-8">
            <h2 className="text-2xl font-bold mb-6 text-center">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-purple-500">
                Détail Comparatif des Services
              </span>
            </h2>
            <div className="rounded-lg shadow-lg">
              <EscortServicesTiers />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EscortSubscription;
